import { useLayoutEffect, useRef } from 'react'
import { gsap, registerGsap } from '../../anim/gsapSetup'
import { useReducedMotion } from '../../anim/useReducedMotion'
import { cn } from '../../lib/cn'

interface RevealProps {
  children: React.ReactNode
  className?: string
  as?: keyof JSX.IntrinsicElements
  y?: number
  delay?: number
  duration?: number
  /** when set, animates direct children with this stagger instead of the wrapper */
  stagger?: number
  start?: string
  /**
   * When false, animates transform ONLY (no opacity), so content is never left at opacity:0
   * waiting on a scroll a crawler may never perform. Use for SEO-critical content.
   */
  fade?: boolean
}

export function Reveal({
  children,
  className,
  as: Tag = 'div',
  y = 28,
  delay = 0,
  duration = 0.9,
  stagger,
  start = 'top 85%',
  fade = true,
}: RevealProps) {
  const ref = useRef<HTMLElement>(null)
  const reduced = useReducedMotion()

  useLayoutEffect(() => {
    const el = ref.current
    if (!el || reduced) return
    registerGsap()
    const ctx = gsap.context(() => {
      const targets = stagger != null ? Array.from(el.children) : [el]
      gsap.from(targets, {
        ...(fade ? { opacity: 0 } : null),
        y,
        duration,
        delay,
        ease: 'power3.out',
        stagger: stagger ?? 0,
        scrollTrigger: { trigger: el, start, once: true },
      })
    }, el)
    return () => ctx.revert()
  }, [reduced, y, delay, duration, stagger, start, fade])

  return (
    // @ts-expect-error dynamic tag with ref
    <Tag ref={ref} className={cn(className)}>
      {children}
    </Tag>
  )
}
